import React, { useEffect, useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Textarea from '../../../components/ui/Textarea';

const COPY_BY_KIND = {
  tone: {
    title: 'Melhorar tom de voz',
    processingTitle: 'Melhorando tom de voz',
    analyzingTitle: 'Analisando tom de voz',
    analyzingMessage:
      'A IA está verificando se o rascunho tem contexto suficiente antes de melhorar.',
    processingMessage: 'A IA está refinando o tom de voz com base no rascunho e nas suas orientações.',
    questionsTitle: 'Algumas perguntas rápidas',
    questionsDescription:
      'Para evitar um resultado genérico, responda o que souber. Você pode pular qualquer pergunta.',
    description:
      'A IA fará apenas os ajustes necessários no rascunho. Se faltar contexto importante, ela fará até 3 perguntas antes de melhorar.',
    placeholder: 'Ex.: tom mais formal, evitar gírias, foco em suporte técnico...',
  },
  instructions: {
    title: 'Melhorar instruções',
    processingTitle: 'Melhorando instruções',
    analyzingTitle: 'Analisando instruções',
    analyzingMessage:
      'A IA está verificando se as instruções têm contexto suficiente antes de melhorar.',
    processingMessage: 'A IA está refinando as instruções com base no rascunho e nas suas orientações.',
    questionsTitle: 'Algumas perguntas rápidas',
    questionsDescription:
      'Para evitar um resultado genérico, responda o que souber. Você pode pular qualquer pergunta.',
    description:
      'A IA fará apenas os ajustes necessários nas instruções. Se faltar contexto importante, ela fará até 3 perguntas antes de melhorar.',
    placeholder: 'Ex.: incluir passos numerados, ser mais objetivo, mencionar quando escalar...',
  },
  mcp_usage_instructions: {
    title: 'Melhorar instruções de uso do MCP',
    processingTitle: 'Melhorando instruções de uso do MCP',
    analyzingTitle: 'Analisando instruções de uso do MCP',
    analyzingMessage:
      'A IA está verificando o rascunho e o catálogo de ferramentas antes de melhorar.',
    processingMessage:
      'A IA está refinando quando e como usar as ferramentas deste servidor MCP.',
    questionsTitle: 'Algumas perguntas rápidas',
    questionsDescription:
      'Para evitar um resultado genérico, responda o que souber sobre o uso das ferramentas. Você pode pular qualquer pergunta.',
    description:
      'A IA fará apenas os ajustes necessários no rascunho e validará as ferramentas no catálogo. Se faltar contexto, ela fará até 3 perguntas.',
    placeholder: 'Ex.: priorizar busca antes de criar registro, evitar chamadas repetidas...',
  },
  prompt_content: {
    title: 'Melhorar conteúdo do prompt',
    processingTitle: 'Melhorando conteúdo do prompt',
    processingMessage:
      'A IA está refinando o system prompt com base no rascunho, metadados do template e nas suas orientações.',
    description:
      'Opcionalmente, descreva o que você quer ajustar no conteúdo. A IA usará essas considerações junto com o rascunho atual.',
    placeholder: 'Ex.: tornar mais conciso, reforçar placeholders, clarificar regras...',
  },
  inbound_router_instructions: {
    title: 'Melhorar instruções de roteamento',
    processingTitle: 'Melhorando instruções de roteamento',
    processingMessage:
      'A IA está refinando as instruções com base no rascunho, nos destinos configurados e nas suas orientações.',
    description:
      'Opcionalmente, descreva o que ajustar nas instruções. A IA considerará os destinos de agente e o rascunho atual.',
    placeholder: 'Ex.: reforçar fallback, priorizar vendas, ser mais objetivo nos critérios...',
  },
  inbound_route_description: {
    title: 'Melhorar descrição do destino',
    processingTitle: 'Melhorando descrição do destino',
    processingMessage:
      'A IA está refinando quando encaminhar mensagens para este destino, com base no rótulo, palavras-chave e contexto.',
    description:
      'Opcionalmente, descreva o que ajustar na descrição. A IA considerará os outros destinos e o rascunho atual.',
    placeholder: 'Ex.: incluir exemplos de intenção, ser mais específico, evitar sobreposição com suporte...',
  },
};

function ProcessingState({ title, message }) {
  return (
    <div className="py-8 flex flex-col items-center text-center">
      <span
        className="h-10 w-10 rounded-full border-[3px] border-violet-200 border-t-violet-600 animate-spin"
        aria-hidden="true"
      />
      <p className="mt-5 text-sm font-medium text-content-primary">{title}</p>
      <p className="mt-2 text-sm text-content-muted max-w-sm">{message}</p>
      <p className="mt-4 text-xs text-content-subtle">Isso pode levar até 2 minutos.</p>
    </div>
  );
}

function ImproveTextModal({
  open,
  kind,
  phase = 'form',
  questions = [],
  initialConsiderations = '',
  isSubmitting,
  error,
  onClose,
  onSubmit,
  onContinueWithAnswers,
  onSkipQuestions,
  onBackToForm,
}) {
  const [considerations, setConsiderations] = useState('');
  const [answersById, setAnswersById] = useState({});
  const copy = COPY_BY_KIND[kind] || COPY_BY_KIND.tone;
  const effectivePhase = isSubmitting && phase === 'form' ? 'improving' : phase;
  const isBusy = effectivePhase === 'analyzing' || effectivePhase === 'improving';

  useEffect(() => {
    if (open && effectivePhase === 'form') {
      setConsiderations(initialConsiderations || '');
      setAnswersById({});
    }
  }, [open, kind, effectivePhase, initialConsiderations]);

  useEffect(() => {
    if (effectivePhase === 'questions') {
      setAnswersById((current) => {
        const next = {};
        questions.forEach((question) => {
          next[question.id] = current[question.id] || '';
        });
        return next;
      });
    }
  }, [effectivePhase, questions]);

  const handleSubmit = () => {
    onSubmit(considerations.trim());
  };

  const handleContinueWithAnswers = () => {
    const answers = questions.map((question) => ({
      id: question.id,
      prompt: question.prompt,
      answer: (answersById[question.id] || '').trim(),
    }));
    onContinueWithAnswers?.({
      considerations: considerations.trim(),
      answers,
    });
  };

  const modalTitle =
    effectivePhase === 'analyzing'
      ? copy.analyzingTitle || copy.processingTitle
      : effectivePhase === 'questions'
        ? copy.questionsTitle || copy.title
        : effectivePhase === 'improving'
          ? copy.processingTitle
          : copy.title;

  return (
    <Modal open={open} onClose={onClose} title={modalTitle} disableClose={isBusy}>
      {effectivePhase === 'analyzing' ? (
        <ProcessingState
          title="Analisando com IA..."
          message={copy.analyzingMessage || copy.processingMessage}
        />
      ) : effectivePhase === 'improving' ? (
        <ProcessingState title="Processando com IA..." message={copy.processingMessage} />
      ) : effectivePhase === 'questions' ? (
        <>
          <p className="text-sm text-content-muted mb-4">
            {copy.questionsDescription ||
              'Responda o que souber para melhorar o resultado. Você pode pular qualquer pergunta.'}
          </p>

          <div className="space-y-4 max-h-[50vh] overflow-auto pr-1">
            {questions.map((question, index) => (
              <Textarea
                key={question.id}
                id={`improve_triage_q_${question.id}`}
                label={`${index + 1}. ${question.prompt}`}
                value={answersById[question.id] || ''}
                onChange={(e) =>
                  setAnswersById((current) => ({
                    ...current,
                    [question.id]: e.target.value,
                  }))
                }
                placeholder="Resposta opcional..."
                rows={3}
                hint={question.why || undefined}
              />
            ))}
          </div>

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              type="button"
              onClick={onBackToForm || onClose}
              className="btn-secondary flex-1"
            >
              Voltar
            </button>
            <button
              type="button"
              onClick={() =>
                onSkipQuestions?.({
                  considerations: considerations.trim(),
                  answers: [],
                })
              }
              className="btn-secondary flex-1"
            >
              Continuar sem responder
            </button>
            <button type="button" onClick={handleContinueWithAnswers} className="btn-primary flex-1">
              Continuar
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-content-muted mb-4">{copy.description}</p>

          <Textarea
            id="improve_text_considerations"
            label="Considerações (opcional)"
            value={considerations}
            onChange={(e) => setConsiderations(e.target.value)}
            placeholder={copy.placeholder}
            rows={4}
            hint="Deixe em branco para melhorar apenas com base no rascunho."
          />

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button type="button" onClick={handleSubmit} className="btn-primary flex-1">
              Melhorar com IA
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default ImproveTextModal;
